import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ContentReport } from '@/hooks/useContentModeration';
import { formatDistanceToNow } from 'date-fns';
import { Eye } from 'lucide-react';

interface ReportCardProps {
  report: ContentReport;
  onViewDetails: (report: ContentReport) => void;
}

const categoryColors: Record<string, string> = {
  hate_speech: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  harassment: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  spam: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  sexual_content: 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400',
  violence: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  misinformation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
};

const contentTypeLabels: Record<string, string> = {
  review_comment: 'Review Comment',
  activity_comment: 'Activity Comment',
  message: 'Message',
};

export function ReportCard({ report, onViewDetails }: ReportCardProps) {
  const getContentText = () => {
    if (!report.content) return '[Content not available]';
    return report.content.comment_text || report.content.content || report.content.message_text || '[No text content]';
  };

  const contentPreview = getContentText().substring(0, 200);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Avatar className="h-8 w-8">
              <AvatarImage src={report.reporter.image || undefined} />
              <AvatarFallback>
                {report.reporter.username?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                @{report.reporter.username} reported {contentTypeLabels[report.content_type]}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className={categoryColors[report.report_category] || categoryColors.other}>
              {report.report_category.replace('_', ' ')}
            </Badge>
            <Badge variant={report.status === 'pending' ? 'default' : 'secondary'}>
              {report.status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {report.additional_details && (
          <div className="text-sm text-muted-foreground">
            <strong>Details:</strong> {report.additional_details}
          </div>
        )}
        
        <div className="p-3 bg-muted rounded-md">
          <p className="text-sm line-clamp-3">{contentPreview}</p>
          {report.content?.author && (
            <p className="text-xs text-muted-foreground mt-2">
              By @{report.content.author.username}
            </p>
          )}
        </div>

        {report.status === 'pending' && (
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => onViewDetails(report)}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              Review & Take Action
            </Button>
          </div>
        )}

        {report.status === 'reviewed' && report.review_notes && (
          <div className="pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground">Review Notes:</p>
            <p className="text-sm mt-1">{report.review_notes}</p>
            {report.reviewer && (
              <p className="text-xs text-muted-foreground mt-1">
                Reviewed by @{report.reviewer.username}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
